
import {defineComponent} from "vue";
import Analytics from "../../utils/analytics";
import FontSelectBlock from "../formblocks/FontSelectBlock.vue";
import FontColorSelectBlock from "../formblocks/FontColorSelectBlock.vue";
import OutlineBlock from "../formblocks/OutlineBlock.vue";
import Checkbox from "../inputs/Checkbox.vue";
import Textarea from "../inputs/Textarea.vue";
import ToggleButton from "../inputs/ToggleButton.vue";
import Fieldset from "../inputs/Fieldset.vue";
import Slider from "../inputs/Slider.vue";
import Select from "../inputs/Select.vue";
import Space from "../global/Space.vue";
import Card from "../global/Card.vue";
import Grid from "../global/Grid.vue";
import GridItem from "../global/GridItem.vue";
import AlignJustify from "../icons/AlignJustify.vue";
import AlignCenter from "../icons/AlignCenter.vue";
import AlignLeft from "../icons/AlignLeft.vue";
import AlignRight from "../icons/AlignRight.vue";

import {ColorStop} from "../../types";
import {absColor} from "../../utils/color";
import {makeTextImage} from "../../utils/textimage";
import {EMOJI_SIZE} from "../../constants/emoji";
import fonts from "../../constants/fonts";
import Input from "../inputs/Input.vue";

type PaddingOption = { label: string, value: number };

const PADDING_OPTIONS = [
  {label: "極小 (推奨)", value: 0.02},
  {label: "大きめ (角丸対応)", value: 0.1},
  {label: "なし", value: 0},
];

export default defineComponent({
  components: {
    Input,
    FontSelectBlock,
    FontColorSelectBlock,
    OutlineBlock,
    Fieldset,
    Slider,
    Select,
    Checkbox,
    Textarea,
    Grid,
    GridItem,
    Space,
    Card,
    ToggleButton,
    AlignJustify,
    AlignCenter,
    AlignLeft,
    AlignRight,
  },
  props: {
    show: {type: Boolean, required: true},
    emojiSize: {type: Number, default: null},
    existingEmojis: {type: Array as () => string[], default: () => []},
  },
  emits: [
    "render",
  ],
  data() {
    return {
      PADDING_OPTIONS,
      conf: {
        /* basic */
        content: "",
        emojiName: "",
        emojiNameValid: false,
        align: "stretch",
        color: "#ffda00",
        gradient: [] as ColorStop[],
        outlines: [] as string[],
        outlineThickness: 8,
        outlineX: 0,
        outlineY: 0,
        padding: PADDING_OPTIONS[0],
        font: fonts[0].fonts[0].value,
        /* advanced */
        lineSpacing: 0.05,
        paddingValue: 0.02,
      },
      showDetails: false,
      /* internals */
      running: false,
      dirty: false,
    };
  },
  computed: {
    absoluteOutlines(): string[] {
      return this.conf.outlines.map((outline) => absColor(outline, this.conf.color));
    },
    absoluteGradient(): { color: string, pos: number }[] {
      return this.conf.gradient.map((cs) => ({
        color: absColor(cs.color, this.conf.color),
        pos: cs.pos,
      }));
    },
    isEmojiNameDuplicate(): boolean {
      if (!this.conf.emojiName) return false;
      return this.existingEmojis.includes(this.conf.emojiName);
    },
  },
  watch: {
    conf: {
      handler(): void {
        Analytics.changeFont(this.conf.font);
        this.render(true);
      },
      deep: true,
    },
    emojiName: {
      handler(): void {
        this.render(true);
      },
    },
    emojiSize: {
      handler(): void {
        this.render(true);
      },
    },
  },
  mounted() {
    Analytics.changeFont(this.conf.font);
  },
  methods: {
    validateEmojiName(value: string): void {
      // Filter out non-alphanumeric characters
      const validatedName = value.replace(/[^a-zA-Z0-9]/g, '');

      // Update the emojiName with the validated value
      this.conf.emojiName = validatedName;

      // Set emojiNameValid based on whether the name is at least 1 character long
      this.conf.emojiNameValid = validatedName.length >= 1;
    },
    preventNonAlphanumeric(event: KeyboardEvent): void {
      // Allow only alphanumeric keys, plus control keys like backspace, delete, arrows, etc.
      const isAlphanumeric = /^[a-zA-Z0-9]$/.test(event.key);
      const isControlKey = event.key.length > 1; // Control keys like Backspace, Delete, arrows, etc.

      if (!isAlphanumeric && !isControlKey) {
        event.preventDefault();
      }
    },
    handleCompositionEnd(event: CompositionEvent): void {
      // This event fires when IME composition is completed
      // We need to validate the input immediately after IME confirmation
      const input = event.target as HTMLInputElement;
      const value = input.value;

      // Filter out non-alphanumeric characters
      const validatedName = value.replace(/[^a-zA-Z0-9]/g, '');

      // Update the input value directly
      input.value = validatedName;

      // Also update our model
      this.conf.emojiName = validatedName;
      this.conf.emojiNameValid = validatedName.length >= 1;

      // Prevent the default behavior to ensure our filtered value is used
      event.preventDefault();
    },
    render(dirty?: boolean): void {
      if (dirty) {
        this.dirty = true;
      }
      if (!this.dirty || this.running) {
        return;
      }
      this.running = true;
      this.dirty = false;
      if (this.conf.content) {
        const canvas = makeTextImage(
            this.conf.content,
            this.conf.color,
            this.conf.font,
            this.emojiSize || EMOJI_SIZE,
            this.conf.align,
            Number(this.conf.lineSpacing),
            this.absoluteOutlines,
            this.conf.outlineThickness,
            this.conf.outlineX,
            this.conf.outlineY,
            this.absoluteGradient,
            Number(this.conf.paddingValue),
        );
        const name = this.conf.content.replace(/\n/g, "");
        const emojiName = this.conf.emojiName.replace(/\n/g, "");
        this.$emit("render", canvas, name, emojiName);
      }
      window.setTimeout(() => {
        this.running = false;
        this.render();
      }, 50);
    },
    selectPadding(padding: PaddingOption): void {
      this.conf.paddingValue = padding.value;
    },
  },
});
